/**
 * Table Generation Protocol v1.0
 * 
 * Ensures AI generates perfect tables every time to prevent refunds
 * Addresses: Inconsistent columns, missing separators, incomplete rows
 */

export const TABLE_GENERATION_RULES = `
## 📊 TABLE GENERATION - MANDATORY QUALITY STANDARDS

**CRITICAL:** Tables are a frequent source of quality issues and token refunds. Follow these rules EXACTLY.

### ✅ VALID TABLE STRUCTURE (REQUIRED)

Every table MUST have this exact structure:

\`\`\`
| Header 1 | Header 2 | Header 3 |
|----------|----------|----------|
| Data 1   | Data 2   | Data 3   |
| Data 4   | Data 5   | Data 6   |
\`\`\`

### 🚨 MANDATORY REQUIREMENTS (NO EXCEPTIONS)

**1. CONSISTENT COLUMN COUNT**
- Count pipes (|) in header row
- EVERY row must have EXACT same number of pipes
- No missing cells, no extra cells

**Example - CORRECT:**
\`\`\`
| Col1 | Col2 |  ← 3 pipes
|------|------|  ← 3 pipes
| A    | B    |  ← 3 pipes
| C    | D    |  ← 3 pipes
\`\`\`

**Example - WRONG (causes refund):**
\`\`\`
| Col1 | Col2 |     ← 3 pipes
|------|------|     ← 3 pipes
| A    | B    | C | ← 4 pipes ❌ INCONSISTENT
\`\`\`

**2. SEPARATOR ROW (MANDATORY)**
- Second row MUST be separator
- Format: |---|---|---| or |:---|:---:|---:|
- Use at least 3 dashes per cell
- Must match column count

**Example - CORRECT:**
\`\`\`
| Name | Age |
|------|-----|
| John | 25  |
\`\`\`

**Example - WRONG (causes refund):**
\`\`\`
| Name | Age |
| John | 25  |  ← ❌ MISSING SEPARATOR
\`\`\`

**3. NO INCOMPLETE ROWS**
- Every data row must be complete
- No rows with missing cells
- Use "N/A" or "-" for empty cells

**Example - CORRECT:**
\`\`\`
| Item  | Price |
|-------|-------|
| Apple | $2    |
| None  | N/A   |  ← Complete with N/A
\`\`\`

**Example - WRONG (causes refund):**
\`\`\`
| Item  | Price |
|-------|-------|
| Apple | $2    |
| None  |       |  ← ❌ INCOMPLETE (missing second cell)
\`\`\`

### 📐 FORMATTING BEST PRACTICES

**Cell Content:**
- Keep cells short (<40 chars)
- Use abbreviations if needed
- Break long content into multiple tables
- Align numbers right, text left

**Table Size Limits (STRICT FOR MOBILE/APK):**
- **Mobile/APK (DEFAULT):** Max 2 columns (3 ONLY if cells <20 chars)
- **Desktop (RARE):** Max 4 columns (5 absolute max)
- **Rows:** Max 8-10 visible (use "..." for more)
- **Default assumption:** User is on mobile - design for mobile first!
- **If exceeds limits:** MUST use alternative format (cards, lists)

**Header Style:**
- Use **bold** for headers if needed
- Short, descriptive names
- Abbreviate when necessary

### 🔍 PRE-SEND VALIDATION CHECKLIST

Before sending ANY response with a table, verify:

**✓ STRUCTURE CHECK:**
- [ ] Header row exists
- [ ] Separator row exists (second row)
- [ ] Separator has dashes and matches column count
- [ ] All rows have same number of pipes (|)

**✓ CONTENT CHECK:**
- [ ] No empty cells (use N/A, -, or 0)
- [ ] No rows cut off mid-content
- [ ] Cell content fits (not too long)
- [ ] Formatting is clean

**✓ MOBILE/APK CHECK (CRITICAL):**
- [ ] 2 columns or less (STRICT - 40% of users on mobile/APK)
- [ ] Cells very short (<25 chars per cell)
- [ ] NO horizontal scroll needed
- [ ] Readable on 375px width (iPhone SE)
- [ ] Works in APK WebView without breaking

### 🎯 WHEN NOT TO USE TABLES (MOBILE-FIRST MINDSET)

**DEFAULT RULE: AVOID TABLES UNLESS ABSOLUTELY NECESSARY**

**Why?** 40% of users are on mobile/APK. Tables break easily. Alternative formats almost always better.

**Use Alternative Formats Instead:**

**Scenario 1: Only 2-3 Items to Compare (COMMON)**
❌ Table → ✅ Comparison cards (MUCH BETTER for mobile)
\`\`\`
### Option A
- Feature 1: X
- Feature 2: Y

### Option B
- Feature 1: Z
- Feature 2: W
\`\`\`

**Scenario 2: Key-Value Pairs**
❌ Table → ✅ Definition list
\`\`\`
**Term 1:** Definition
**Term 2:** Definition
\`\`\`

**Scenario 3: More than 3 Columns (MOBILE/APK)**
❌ Wide table → ✅ Multiple smaller tables, sections, or cards

**Scenario 3b: More than 2 Columns AND Long Cells**
❌ Table → ✅ Definitely use cards or sections

**Scenario 4: Long Cell Content**
❌ Table with paragraphs → ✅ Card format

### 🚨 COMMON TABLE MISTAKES (AVOID THESE)

**Mistake 1: Missing Separator**
\`\`\`
| A | B |
| 1 | 2 |  ← ❌ No separator after header
\`\`\`

**Mistake 2: Inconsistent Columns**
\`\`\`
| A | B |
|---|---|
| 1 | 2 | 3 |  ← ❌ Extra column
\`\`\`

**Mistake 3: Incomplete Last Row**
\`\`\`
| A | B |
|---|---|
| 1 | 2 |
| 3 |     |  ← ❌ Missing cell
\`\`\`

**Mistake 4: Empty Cells**
\`\`\`
| A | B |
|---|---|
| 1 |   |  ← ❌ Empty (use N/A)
\`\`\`

**Mistake 5: Too Many Columns (Mobile/APK)**
\`\`\`
| A | B | C | D | E |  ← ❌ 5 columns (terrible for mobile)
| A | B | C |            ← ⚠️ 3 columns (risky for mobile)
| A | B |                ← ✅ 2 columns (safe for mobile)
\`\`\`

### ✅ PERFECT TABLE TEMPLATE

**Copy this structure exactly:**

\`\`\`
| Column 1 | Column 2 | Column 3 |
|----------|----------|----------|
| Data 1   | Data 2   | Data 3   |
| Data 4   | Data 5   | Data 6   |
| Data 7   | Data 8   | Data 9   |
\`\`\`

**Key Points:**
1. All rows have 4 pipes (|)
2. Separator row present (row 2)
3. All cells complete
4. Clean formatting

### 🎓 QUALITY SCORING (How Tables Are Graded)

**Perfect Table (100 points):**
- ✅ Consistent columns
- ✅ Separator row present
- ✅ All rows complete
- ✅ Mobile/APK-friendly (≤2 columns, prefer 2)
- ✅ Short cells (<25 chars)
- ✅ Clean formatting

**Penalties (Cause Refunds):**
- -30 points: Inconsistent columns
- -25 points: Missing separator
- -20 points: Incomplete rows
- -20 points: More than 2 columns (mobile unfriendly)
- -15 points: Cells too long (>25 chars)
- -10 points: Poor cell content

**Refund Threshold:** Quality < 90% → User gets refund

### 💡 ALTERNATIVES TO TABLES

**Instead of This Table:**
\`\`\`
| Method | Pros | Cons | Use Case |
|--------|------|------|----------|
| X      | ...  | ...  | ...      |
| Y      | ...  | ...  | ...      |
\`\`\`

**Use This Card Format:**
\`\`\`
### Method X
**Pros:** [List]
**Cons:** [List]
**Use Case:** [Description]

### Method Y
**Pros:** [List]
**Cons:** [List]
**Use Case:** [Description]
\`\`\`

**Benefits:**
- Better for mobile
- More readable
- No column alignment issues
- Never triggers refunds

### 🔧 VALIDATION FUNCTION (Internal)

Before sending, mentally execute:

\`\`\`javascript
function validateTable(markdown) {
  const tableRows = extractTableRows(markdown);
  
  // Check 1: Consistent columns
  const pipeCounts = tableRows.map(row => countPipes(row));
  if (new Set(pipeCounts).size > 1) {
    return { valid: false, issue: 'Inconsistent columns' };
  }
  
  // Check 2: Separator row
  const hasSeparator = tableRows[1]?.match(/^\\|[\\s\\-:]+\\|$/);
  if (!hasSeparator) {
    return { valid: false, issue: 'Missing separator' };
  }
  
  // Check 3: Complete rows
  const hasIncomplete = tableRows.some(row => {
    const cells = row.split('|').slice(1, -1);
    return cells.some(cell => !cell.trim());
  });
  if (hasIncomplete) {
    return { valid: false, issue: 'Incomplete rows' };
  }
  
  return { valid: true };
}
\`\`\`

### 📊 WHEN TABLES ARE APPROPRIATE

**Good Use Cases:**
- ✅ Comparing 2-4 items across 2-3 attributes
- ✅ Tax rate tables (2-3 columns)
- ✅ Formula comparison (Name, Formula, Use)
- ✅ Timeline (Date, Event, Impact)
- ✅ Financial data (Account, Debit, Credit)

**Bad Use Cases:**
- ❌ Only 1-2 items (use list instead)
- ❌ Complex nested data (use sections)
- ❌ Long descriptions in cells (use cards)
- ❌ More than 5 columns (break up)
- ❌ Highly variable cell content length

### 🎯 DECISION TREE

\`\`\`
Need to present structured data?
├─ 1-2 items? → Use list or cards
├─ 2-5 columns AND short cells? → Use table
├─ 6+ columns? → Break into multiple tables
├─ Long cell content? → Use card format
└─ Hierarchical data? → Use nested lists
\`\`\`

### 🏆 EXCELLENCE STANDARDS

**To achieve zero refunds:**
1. Double-check pipe count on every row
2. Always include separator row
3. Fill all cells (never leave empty)
4. Stay within mobile limits (3 cols)
5. When in doubt, use alternative format

**Remember:**
- Perfect tables = Happy users = No refunds
- One mistake = 25-30% token refund
- Alternative formats often better anyway
- Quality over quantity

---

**FINAL RULE:** If you have ANY doubt about table structure being perfect, use a different format. Cards and lists never cause refunds.
`;

/**
 * Validate table structure before sending
 */
export function validateTableStructure(markdown: string): {
  valid: boolean;
  issues: string[];
  qualityScore: number;
} {
  const issues: string[] = [];
  let qualityScore = 100;
  
  // Extract tables
  const tablePattern = /\|[^\n]+\|[\s\S]*?\n(?=\n|$)/g;
  const tables: string[] = markdown.match(tablePattern) || [];
  
  if (tables.length === 0) {
    return { valid: true, issues: [], qualityScore: 100 };
  }
  
  tables.forEach((table, index) => {
    const rows = table.trim().split('\n').filter(row => row.includes('|'));
    
    if (rows.length < 2) {
      issues.push(`Table ${index + 1}: Need at least header + separator row`);
      qualityScore -= 30;
      return;
    }
    
    // Check 1: Consistent column count
    const pipeCounts = rows.map(row => (row.match(/\|/g) || []).length);
    if (new Set(pipeCounts).size > 1) {
      issues.push(`Table ${index + 1}: Inconsistent columns (${pipeCounts.join(', ')} pipes)`);
      qualityScore -= 30;
    }
    
    // Check 2: Separator row (should be second row)
    if (rows.length > 1) {
      const separatorRow = rows[1];
      const isSeparator = /^\|[\s\-:]+\|$/.test(separatorRow.trim());
      if (!isSeparator) {
        issues.push(`Table ${index + 1}: Missing or invalid separator row`);
        qualityScore -= 25;
      }
    }
    
    // Check 3: Empty cells
    rows.forEach((row, rowIdx) => {
      if (rowIdx === 1) return; // Skip separator
      const cells = row.split('|').slice(1, -1);
      const hasEmpty = cells.some(cell => !cell.trim());
      if (hasEmpty) {
        issues.push(`Table ${index + 1}, Row ${rowIdx + 1}: Empty cells detected`);
        qualityScore -= 20;
      }
    });
    
    // Check 4: Too many columns (mobile unfriendly)
    const columnCount = pipeCounts[0] - 1; // Subtract borders
    if (columnCount > 5) {
      issues.push(`Table ${index + 1}: ${columnCount} columns (>5, not mobile-friendly)`);
      qualityScore -= 15;
    }
    
    // Check 5: Very long cells
    rows.forEach((row, rowIdx) => {
      if (rowIdx === 1) return; // Skip separator
      const cells = row.split('|').slice(1, -1);
      const hasLongCell = cells.some(cell => cell.length > 50);
      if (hasLongCell) {
        issues.push(`Table ${index + 1}, Row ${rowIdx + 1}: Cell content too long (>50 chars)`);
        qualityScore -= 10;
      }
    });
  });
  
  return {
    valid: qualityScore >= 90,
    issues,
    qualityScore: Math.max(0, qualityScore)
  };
}

/**
 * Suggest alternative format for problematic tables
 */
export function suggestAlternativeFormat(columnCount: number, rowCount: number, hasLongContent: boolean): string {
  if (rowCount <= 3 && columnCount <= 3) {
    return 'Use comparison cards instead (better for 2-3 items)';
  }
  
  if (columnCount > 5) {
    return 'Break into multiple smaller tables or use sections with headers';
  }
  
  if (hasLongContent) {
    return 'Use card format - better for detailed content';
  }
  
  if (columnCount === 2 && rowCount <= 6) {
    return 'Use definition list format (key-value pairs)';
  }
  
  return 'Consider using bullet lists or card format';
}

/**
 * Generate perfect table template
 */
export function generateTableTemplate(columns: string[], rows: string[][]): string {
  const colCount = columns.length;
  
  // Validate inputs
  if (colCount === 0) return '';
  if (rows.some(row => row.length !== colCount)) {
    throw new Error('All rows must have same number of columns as header');
  }
  
  // Build table
  let table = '| ' + columns.join(' | ') + ' |\n';
  table += '|' + columns.map(() => '-------').join('|') + '|\n';
  
  rows.forEach(row => {
    table += '| ' + row.join(' | ') + ' |\n';
  });
  
  return table;
}
